import { useMemo, useState } from "react"

import { DateTime } from "luxon"

import DateTimePicker from "@/components/DateTimePicker"
import { Label } from "@/components/ui/label"
import IncentiveRow from "@/features/incentives/components/IncentiveRow.tsx"
import type { IncentiveRowConfig } from "@/features/incentives/types/incentiveRowTypes.ts"

export default function Incentives() {
  const [asOf, setAsOf] = useState<Date>(
    DateTime.now().startOf("day").minus({ days: 1 }).toJSDate(),
  )

  const incentiveConfig: IncentiveRowConfig[] = useMemo(() => {
    const monthEnd = DateTime.fromJSDate(asOf).endOf("month").toJSDate()
    return [
      {
        location: 2,
        gaugeLabel: "BCO Production Target",
        opExLabel: "BCO OpEx Target",
        opExTarget: 1660839,
        asOf,
        targetKpi: {
          $type: "basic",
          id: 80,
          location: 2,
        },
        kpis: {
          asOf,
          returnSize: 7,
          aggType: "Daily",
          kpis: [
            {
              $type: "calc",
              expression: "([10]+[20])",
              name: "Manure to Receiving",
              aggFunction: "Sum",
              unit: "Gallons",
              typeId: 1,
              location: 2,
            },
            {
              $type: "basic",
              id: 45,
              typeId: 1,
              location: 2,
            },
            {
              $type: "basic",
              id: 60,
              typeId: 1,
              location: 2,
            },
            {
              $type: "basic",
              id: 80,
              location: 2,
            },
            {
              $type: "calc",
              expression: "[110]/24 * 100",
              name: "Plant Uptime",
              location: 2,
              unit: "%",
            },
          ],
        },
      },
      {
        location: 3,
        gaugeLabel: "SPL Production Target",
        opExLabel: "SPL OpEx Target",
        opExTarget: 557109,
        asOf,
        targetKpi: {
          $type: "basic",
          id: 10,
          location: 3,
          aggFunction: "Sum",
        },
        kpis: {
          returnSize: 7,
          aggType: "Daily",
          asOf,
          kpis: [
            {
              $type: "basic",
              id: 10,
              location: 3,
            },
            {
              $type: "basic",
              id: 11,
              typeId: 1,
              location: 3,
            },
            {
              $type: "calc",
              preAggregate: true,
              expression: "[10]/[11]",
              name: "Gallons/Load",
              unit: "Gallons",
              typeId: 1,
              location: 3,
            },
            {
              $type: "basic",
              id: 350,
              typeId: 1,
              location: 3,
            },
            {
              $type: "basic",
              id: 340,
              typeId: 1,
              location: 3,
            },
          ],
        },
      },
      {
        location: 4,
        gaugeLabel: "PLTH Production Target",
        opExLabel: "PLTH OpEx Target",
        opExTarget: 257463,
        asOf: monthEnd,
        targetKpi: {
          $type: "basic",
          id: 80,
          location: 4,
        },
        kpis: {
          returnSize: 7,
          asOf,
          kpis: [
            {
              $type: "basic",
              id: 60,
              typeId: 1,
              location: 4,
            },
            {
              $type: "basic",
              id: 65,
              typeId: 1,
              location: 4,
            },
            {
              $type: "basic",
              id: 61,
              typeId: 1,
              location: 4,
            },
            {
              $type: "basic",
              id: 80,
              location: 4,
            },
            {
              $type: "calc",
              expression: "([110]/24)*100",
              name: "Plant Uptime",
              unit: "%",
              location: 4,
              aggFunction: "Average",
            },
          ],
        },
      },
      {
        location: 5,
        gaugeLabel: "LWRN Production Target",
        opExLabel: "LWRN OpEx Target",
        opExTarget: 389690,
        asOf: monthEnd,
        targetKpi: {
          $type: "basic",
          id: 80,
          location: 5,
        },
        kpis: {
          returnSize: 7,
          asOf,
          kpis: [
            {
              $type: "basic",
              id: 60,
              typeId: 1,
              location: 5,
            },
            {
              $type: "basic",
              id: 65,
              typeId: 1,
              location: 5,
            },
            {
              $type: "basic",
              id: 61,
              typeId: 1,
              location: 5,
            },
            {
              $type: "basic",
              id: 80,
              location: 5,
            },
            {
              $type: "calc",
              expression: "([110]/24)*100",
              name: "Plant Uptime",
              unit: "%",
              location: 5,
              aggFunction: "Average",
            },
          ],
        },
      },
    ]
  }, [asOf])
  return (
    <div className="flex flex-col gap-2">
      <div className="p-1 flex gap-2">
        <Label className="text-muted-foreground font-semibold">As Of:</Label>
        <DateTimePicker
          dateOnly={true}
          value={asOf}
          onChange={(d) => setAsOf(d || new Date())}
          className="w-[200px]"
        />
      </div>
      {incentiveConfig.map((row, i) => (
        <IncentiveRow key={i} config={row} />
      ))}
    </div>
  )
}
